import {
    ATTRIBUTES,
    openBackInStockModal,
    PUB_SUB_EVENTS,
    SELECTORS,
    createATCitems
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { publish, getQuickAddPopup } from '@/scripts/core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
    addItemsToCart,
    getCartAttributesFromFormData,
    getDOMCartSectionApiIds,
    renderResponseToCartDrawer
} from '@/scripts/core/cart-functions';
import { applyStoqPreorderProperties } from '@/scripts/product/stoq-selling-plan'
import { SizeGuide } from './size-guide';

type BundlePackItem = {
    id: number
    quantity: number
}

type BundlePackCharm = {
    src: string
    alt: string
}

const QUICK_ADD_POPUP_SELECTORS = {
    container: '.quick-add-popup-container',
    content: '.quick-add-popup-content',
    overlay: '.quick-add-popup-overlay',
}

let quickAddPopupOpenFrame: number | null = null

const onQuickAddPopupDocumentKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' && event.code !== 'Escape') return
    closeQuickAddPopup()
}

function getQuickAddPopupContainer() {
    return q.os<HTMLElement>(QUICK_ADD_POPUP_SELECTORS.container, document)
}

function getQuickAddPopupContent() {
    return q.os<HTMLElement>(QUICK_ADD_POPUP_SELECTORS.content, document)
}

function bindQuickAddPopupOverlay(container: HTMLElement) {
    const overlay = q.os<HTMLButtonElement>(QUICK_ADD_POPUP_SELECTORS.overlay, container)
    if (!overlay || overlay.dataset.ucBound === 'true') return
    overlay.dataset.ucBound = 'true'
    overlay.addEventListener('click', () => {
        closeQuickAddPopup()
    })
}

function openQuickAddPopup(content: string) {
    const container = getQuickAddPopupContainer()
    const popupContent = getQuickAddPopupContent()
    if (!container || !popupContent) return
    bindQuickAddPopupOverlay(container)
    if (quickAddPopupOpenFrame != null) {
        cancelAnimationFrame(quickAddPopupOpenFrame)
    }
    container.classList.remove('active')
    popupContent.innerHTML = content
    document.body.classList.add('overflow-hidden')
    document.addEventListener('keydown', onQuickAddPopupDocumentKeydown)
    quickAddPopupOpenFrame = requestAnimationFrame(() => {
        quickAddPopupOpenFrame = null
        container.classList.add('active')
    })
}

function closeQuickAddPopup() {
    const container = getQuickAddPopupContainer()
    const popupContent = getQuickAddPopupContent()
    if (!container || !popupContent) return
    if (quickAddPopupOpenFrame != null) {
        cancelAnimationFrame(quickAddPopupOpenFrame)
        quickAddPopupOpenFrame = null
    }
    popupContent.innerHTML = ''
    container.classList.remove('active')
    document.body.classList.remove('overflow-hidden')
    document.removeEventListener('keydown', onQuickAddPopupDocumentKeydown)
}

export class QuickAddPopup extends UcoastEl {
    static htmlSelector = 'quick-add-popup'
    static selectors = {
        form: 'form',
        submitButton: '[type="submit"]',
        colorInputs: '[name="quick-add-popup-color-input"]',
        selectedColorEl: '.quick-add-popup__color-selected',
        genderInputs: '[name="quick-add-popup-gender"]',
        sizeButtons: '.quick-add-popup__size-button',
        sizeChartToggle: '.quick-add-popup__size-chart',
        popupContainer: '.quick-add-popup-container',
        popupContent: '.quick-add-popup-content',
        formIdEl: '[name="id"]'
    }

    form: HTMLFormElement
    formIdEl: HTMLInputElement
    colorInputs?: NodeListOf<HTMLInputElement> 
    selectedColorEl?: HTMLElement
    genderInputs: NodeListOf<HTMLInputElement>
    sizeButtons: NodeListOf<HTMLButtonElement>
    sizeChartToggle?: HTMLButtonElement
    hideErrors: boolean
    error?: boolean

    constructor() {
        super()

        this.form = q.rs(QuickAddPopup.selectors.form, this)
        this.formIdEl = (this.form.querySelector('[name="id"]') as HTMLInputElement) || (this.form.querySelector('[name="items[0][id]"]') as HTMLInputElement)
        this.genderInputs = q.rl(QuickAddPopup.selectors.genderInputs, this)
        this.sizeButtons = q.rl(QuickAddPopup.selectors.sizeButtons, this)
        this.sizeChartToggle = q.os(QuickAddPopup.selectors.sizeChartToggle, this)
        this.selectedColorEl = this.querySelector(QuickAddPopup.selectors.selectedColorEl) as HTMLElement
        this.colorInputs = this.querySelectorAll(QuickAddPopup.selectors.colorInputs)

        this.colorInputs?.forEach(input => {
            input.addEventListener('change', this.onChangeColor.bind(this))
        })
        this.genderInputs.forEach(input => {
            input.addEventListener('change', this.onChangeGender.bind(this))
        })
        this.sizeButtons.forEach(button => {
            button.addEventListener('click', this.onSubmitHandler.bind(this))
        })
        this.sizeChartToggle?.addEventListener('click', (e: Event) => {
            e.preventDefault()
            const sizeGuide = document.querySelector('size-guide') as SizeGuide
            if(sizeGuide) sizeGuide.open()
        })

        this.hideErrors = this.dataset.hideErrors === 'true'
        this.renderBundlePackSummary()
        this.hideBundlePackPreorderLabels()
        this.updateBundlePackSelectedSize()
    }

    private isBundlePackPopup() {
        return this.dataset.ucBundlePackItems != null
    }

    private getBundlePackItems(): BundlePackItem[] {
        const raw = this.dataset.ucBundlePackItems
        if (!raw) return []

        try {
            const parsed = JSON.parse(raw)
            if (!Array.isArray(parsed)) return []

            return parsed
                .map((item) => ({
                    id: Number(item?.id),
                    quantity: Number(item?.quantity || 1),
                }))
                .filter((item) => Number.isFinite(item.id) && item.id > 0 && Number.isFinite(item.quantity) && item.quantity > 0)
        } catch (error) {
            console.error('Invalid bundle pack payload', error)
            return []
        }
    }

    private getBundlePackCharms(): BundlePackCharm[] {
        const raw = this.dataset.ucBundlePackCharms
        if (!raw) return []

        try {
            const parsed = JSON.parse(raw)
            if (!Array.isArray(parsed)) return []

            return parsed
                .map((item) => ({
                    src: String(item?.src || ''),
                    alt: String(item?.alt || ''),
                }))
                .filter((item) => item.src !== '')
        } catch (error) {
            console.error('Invalid bundle pack charm payload', error)
            return []
        }
    }

    private getBundlePackMetaLine() {
        return this.dataset.ucBundlePackMetaLine?.trim() || ''
    }

    private getBundlePackSizingMessage() {
        return this.dataset.ucBundlePackSizingMessage?.trim() || ''
    }

    private applyBundlePackItems(formData: FormData) {
        const bundleItems = this.getBundlePackItems()
        if (bundleItems.length === 0) return

        const baseVariantId = String(formData.get('items[0][id]') || formData.get('id') || '')
        if (!baseVariantId) return

        const existingItemIndexes = Array.from(formData.keys())
            .map((key) => key.match(/^items\[(\d+)\]\[id\]$/))
            .filter((match): match is RegExpMatchArray => match !== null)
            .map((match) => Number(match[1]))
            .filter((value) => Number.isFinite(value))

        if (existingItemIndexes.length === 0) {
            formData.set('items[0][id]', baseVariantId)
            formData.set('items[0][quantity]', '1')
            formData.delete('id')
        }

        const sellingPlan = formData.get('selling_plan')
        if (sellingPlan && !formData.get('items[0][selling_plan]')) {
            formData.set('items[0][selling_plan]', String(sellingPlan))
            formData.delete('selling_plan')
        }

        const buildId = `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        formData.set('items[0][properties[_charms_build_id]]', buildId)
        formData.set('items[0][properties[_has_customization]]', 'true')
        formData.set('items[0][properties[_hide_cart_line_item_props]]', 'true')
        formData.delete('items[0][properties[PREORDER]]')
        formData.delete('items[0][properties[_preorder]]')
        formData.delete('items[0][properties[_stoq_shipping_text]]')

        let nextIndex =
            existingItemIndexes.length > 0
                ? Math.max(...existingItemIndexes) + 1
                : 1

        for (const item of bundleItems) {
            formData.set(`items[${nextIndex}][id]`, String(item.id))
            formData.set(`items[${nextIndex}][quantity]`, String(item.quantity))
            formData.set(`items[${nextIndex}][parent_id]`, baseVariantId)
            formData.set(`items[${nextIndex}][properties[_parent_id]]`, baseVariantId)
            formData.set(`items[${nextIndex}][properties[_parent_build_id]]`, buildId)
            formData.set(`items[${nextIndex}][properties[_hide_cart_line_item_props]]`, 'true')
            formData.delete(`items[${nextIndex}][properties[PREORDER]]`)
            formData.delete(`items[${nextIndex}][properties[_preorder]]`)
            formData.delete(`items[${nextIndex}][properties[_stoq_shipping_text]]`)
            nextIndex += 1
        }
    }

    private hideBundlePackPreorderLabels() {
        if (!this.isBundlePackPopup() || this.dataset.ucHidePreorderLabels !== 'true') return

        for (const button of this.sizeButtons) {
            if (button.dataset.preorder !== 'true') continue
            button.classList.add('quick-add-popup__size-button--bundle-pack')
            q.os('.sold-out-label', button)?.remove()
        }
    }

    private getVisibleSizeLabel(button: HTMLButtonElement) {
        const visibleLabel = Array.from(button.querySelectorAll<HTMLElement>('.quick-add-popup-size__label-text'))
            .find((label) => !label.classList.contains('hidden'))
        return visibleLabel?.textContent?.trim() || ''
    }

    private updateBundlePackSelectedSize(button?: HTMLButtonElement) {
        if (!this.isBundlePackPopup()) return

        const selectedSizeEl = q.os<HTMLElement>('[data-uc-bundle-pack-size]', this)
        if (!selectedSizeEl) return

        const sourceButton = button || this.sizeButtons[0]
        if (!sourceButton) return

        const sizeLabel = this.getVisibleSizeLabel(sourceButton)
        if (sizeLabel) {
            selectedSizeEl.textContent = sizeLabel
        }
    }

    private renderBundlePackSummary() {
        if (!this.isBundlePackPopup()) return

        const heading = q.os<HTMLElement>('.quick-add-popup__heading', this)
        if (!heading || q.os('.quick-add-popup__bundle-pack-summary', this)) return

        const summary = document.createElement('div')
        summary.className = 'quick-add-popup__bundle-pack-summary'

        const metaLine = this.getBundlePackMetaLine()
        if (metaLine !== '') {
            const meta = document.createElement('p')
            meta.className = 'quick-add-popup__bundle-pack-meta h5 m-0'

            const metaText = document.createElement('span')
            metaText.textContent = metaLine
            meta.append(metaText)

            const dot = document.createElement('span')
            dot.className = 'quick-add-popup__bundle-pack-dot'
            dot.textContent = '·'
            meta.append(dot)

            const size = document.createElement('span')
            size.setAttribute('data-uc-bundle-pack-size', '')
            meta.append(size)

            summary.append(meta)
        }

        const charms = this.getBundlePackCharms()
        if (charms.length > 0) {
            const charmList = document.createElement('div')
            charmList.className = 'quick-add-popup__bundle-pack-charms'

            for (const charm of charms) {
                const charmItem = document.createElement('div')
                charmItem.className = 'quick-add-popup__bundle-pack-charm'

                const charmImage = document.createElement('img')
                charmImage.className = 'quick-add-popup__bundle-pack-charm-image'
                charmImage.src = charm.src
                charmImage.alt = charm.alt
                charmImage.width = 35
                charmImage.height = 35
                charmImage.loading = 'lazy'
                charmImage.decoding = 'async'

                charmItem.append(charmImage)
                charmList.append(charmItem)
            }

            summary.append(charmList)
        }

        const sizingMessage = this.getBundlePackSizingMessage()
        if (sizingMessage !== '') {
            const message = document.createElement('p')
            message.className = 'quick-add-popup__bundle-pack-sizing-message text-body m-0'
            message.textContent = sizingMessage
            summary.append(message)
        }

        heading.insertAdjacentElement('afterend', summary)
    }

    async onChangeColor(e: Event) {
        e.preventDefault()
        const selectedColorInput = q.rs('[name="quick-add-popup-color-input"]:checked', this) as HTMLInputElement
        if(!selectedColorInput) return
        const selectedColor = selectedColorInput.value
        if (this.selectedColorEl) this.selectedColorEl.textContent = selectedColor
        const url = selectedColorInput.dataset.url
        if(!url) return
        const popup = await getQuickAddPopup(url)
        const popupContent = getQuickAddPopupContent()
        if (!popupContent) return
        popupContent.innerHTML = popup.outerHTML
    }

    onChangeGender(e: Event) {
        e.preventDefault()
        const selectedGenderInput = q.rs('[name="quick-add-popup-gender"]:checked', this) as HTMLInputElement
        if(!selectedGenderInput) return

        const selectedGender = selectedGenderInput.value
        q.rl('.quick-add-popup-size__label-text[data-gender]', this).forEach(text => {
            text.classList.toggle('hidden', text.dataset.gender != selectedGender)
        })
        this.updateBundlePackSelectedSize()
    }

    getSpinner(target: HTMLButtonElement) {
        return q.rs(SELECTORS.loadingOverlaySpinner, target)
    }

    onSubmitHandler(event: Event) {
        event.preventDefault()
        const target = event.currentTarget as HTMLButtonElement
        if(!target) return
        const variantId = target.dataset.variantId
        if(!variantId) return
        this.formIdEl.value = variantId
        const bisVariantId = q.oa(target, 'data-uc-bis-variant-id')
        const oosUrl = q.oa(target, 'data-oos-popup-trigger')
        if (bisVariantId) {
            openBackInStockModal({
                opener: target,
                klaviyoUrl: oosUrl,
                variantId: bisVariantId,
            })
            return
        }
        const preorder = target.dataset.preorder == 'true'

        this.updateBundlePackSelectedSize(target)
        target.setAttribute('aria-disabled', 'true')
        target.setAttribute(ATTRIBUTES.loading, '')
        this.getSpinner(target).classList.remove('hidden')

        const formData = new FormData(this.form)
        if (formData.has('quick-add-popup-gender')) {
            formData.delete('quick-add-popup-gender')
        }
        this.applyBundlePackItems(formData)
        let formVariantId = formData.get('id') || formData.get('items[0][id]')
        if (!formVariantId) throw Error('No variant id found')
        const addedVariantId = parseInt(formVariantId.toString())

        type AddToCartItem = {
            id: number
            quantity: number
            selling_plan?: number
            properties: Record<string, string>
            parent_id?: number
        }
        let items: AddToCartItem[] = []
        if(formData.get('items[0][id]')) {
            applyStoqPreorderProperties({
                formData,
                preorder,
            })
            items = createATCitems(formData) as AddToCartItem[]
        } else {
            applyStoqPreorderProperties({
                formData,
                preorder,
            })
        }
        const cartAttributes = getCartAttributesFromFormData(formData)

        addItemsToCart(
            items.length ? items : formData,
            getDOMCartSectionApiIds(),
            cartAttributes
        )
            .then((cart) => {
                if ('status' in cart) {
                    return
                }

                publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form',
                    productVariantId: addedVariantId,
                })
                renderResponseToCartDrawer(cart)
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => {
                target.removeAttribute(ATTRIBUTES.loading)

                if (!this.error)
                    target.removeAttribute('aria-disabled')
                this.getSpinner(target).classList.add('hidden')
            })
    }
}

export class QuickAddToggle extends UcoastEl {
    static htmlSelector = 'quick-add-toggle'
    toggleButton: HTMLButtonElement

    constructor() {
        super()
        this.toggleButton = q.rs('button', this)
        this.toggleButton.addEventListener('click', this.openPopup.bind(this))
    }

    async openPopup() {
        const url = this.dataset.url
        if(!url) return
        const popup = await getQuickAddPopup(url)
        openQuickAddPopup(popup.outerHTML)
    }
}

export class BundlePackQuickAddTrigger extends UcoastEl {
    static htmlSelector = 'bundle-pack-quick-add-trigger'
    triggerButton: HTMLButtonElement

    constructor() {
        super()
        this.triggerButton = q.rs('button', this)
        this.triggerButton.addEventListener('click', this.openPopup.bind(this))
    }

    async openPopup(event: Event) {
        event.preventDefault()
        const url = this.dataset.url
        if (!url) return

        const popup = await getQuickAddPopup(url)
        popup.dataset.ucBundlePackItems = this.dataset.ucBundlePackItems || '[]'
        popup.dataset.ucBundlePackCharms = this.dataset.ucBundlePackCharms || '[]'
        popup.dataset.ucBundlePackMetaLine = this.dataset.ucBundlePackMetaLine || ''
        popup.dataset.ucBundlePackSizingMessage = this.dataset.ucBundlePackSizingMessage || ''
        popup.dataset.ucHidePreorderLabels = this.dataset.ucHidePreorderLabels || 'false'

        const heading = q.os<HTMLElement>('.quick-add-popup__heading', popup)
        if (heading && this.dataset.ucBundlePackTitle) {
            heading.textContent = this.dataset.ucBundlePackTitle
        }

        q.ol<HTMLElement>('.quick-add-popup__color-label, .quick-add-popup__color-list', popup)
            ?.forEach((element) => element.remove())

        openQuickAddPopup(popup.outerHTML)
    }
}

export class QuickAddPopupClose extends UcoastEl {
    static htmlSelector = 'quick-add-popup-close'
    closeButton: HTMLButtonElement

    constructor() {
        super()
        this.closeButton = q.rs('button', this)
        this.closeButton.addEventListener('click', this.closePopup.bind(this))
    }

    closePopup() {
        closeQuickAddPopup()
    }
}
